import React, {useState} from 'react';
import {useTranslation} from 'react-i18next';
import {ManageAssetsStep} from '../Step/ManageAssetsStep';
import {UserFlowStep} from '../../../Facade/UserFlow/BaseUserFlow';
import {AssetMetaInterface, CoreProjectService} from '@taopix/taopix-core/dist/types/types';
import {MediaGalleryProps} from '../../MediaGallery/MediaGallery';
import {useTaopix} from '../../Context/TaopixContext';
import {LoadingOverlay} from '@taopix/taopix-design-system';
import {useUserFlowState} from '../../Context/UserFlow/UserFlowContext';
import {useErrorBoundary} from 'react-error-boundary';
import {UserFlowType} from '../../../Facade/UserFlow/UserFlowFacade';
import {CalendarSettingsStep} from './Step/CalendarSettingsStep';
import useSettings from '../../../Hooks/useSettings';
import {useNavigate} from 'react-router-dom';
import {AssistantCommonProps, AssistantHeaderProps, AssistantNavigationProps} from '../Assistant';

type SmartDesignAssistantProps = {
  assets: AssetMetaInterface[];
  mediaGalleryProps: MediaGalleryProps;
  loader: CoreProjectService;
  onAfterRun: () => void;
} & AssistantCommonProps &
  AssistantNavigationProps &
  AssistantHeaderProps;

export const CalendarAssistant = ({
  assets,
  loader,
  onAfterRun,
  ...props
}: SmartDesignAssistantProps) => {
  const taopix = useTaopix();
  const userFlow = useUserFlowState();
  const settings = useSettings();
  const {showBoundary} = useErrorBoundary();
  const navigate = useNavigate();
  const {t} = useTranslation(['assistants']);
  const stateManager = taopix.core.get('stateManager');

  const [switchInProgress, setSwitchInProgress] = useState<boolean>(false);

  // if the calendar settings step is not there then disable back button as there is nothing to go back to
  const disableBackButton = !userFlow
    .getSteps(UserFlowType.Assistant)
    .includes(`${UserFlowType.Assistant}.${UserFlowStep.CalendarSettings}`);

  /**
   * Loads the required data and switch to the Editor without reloading the page.
   */
  const onContinueInEditor = (): void => {
    loader
      .switch('editor', settings.state.tokens.session)
      .then(response => {
        navigate('/');
        userFlow.setStage(UserFlowType.UserFlow);
      })
      .finally(() => {
        setSwitchInProgress(false);
        userFlow.setCurrentStep(`${UserFlowType.Assistant}.${UserFlowStep.None}`);
      });
  };

  const getBackButton = (): JSX.Element => {
    return React.cloneElement(props.backButton, {
      ...props.backButton.props,
      onClick: props.backButton.props.onClick,
      disabled: disableBackButton,
    });
  };

  const getNextButton = (): JSX.Element => {
    return React.cloneElement(props.nextButton, {
      ...props.nextButton.props,
      onClick: () => {
        setSwitchInProgress(true);
        stateManager
          .call('buildcalendar')
          .then(response => {
            onAfterRun();
            onContinueInEditor();
          })
          .catch(error => {
            setSwitchInProgress(false);
            showBoundary(error);
          });
      },
    });
  };

  let currentStep = <></>;
  switch (true) {
    case userFlow.isStepActive(UserFlowType.Assistant, UserFlowStep.CalendarSettings):
      currentStep = <CalendarSettingsStep assets={assets} {...props} />;
      break;
    case userFlow.isStepActive(UserFlowType.Assistant, UserFlowStep.AssistantManageAssets):
      currentStep = (
        <ManageAssetsStep
          assets={assets}
          {...props}
          backButton={getBackButton()}
          nextButton={getNextButton()}
        />
      );
      break;

    default:
      break;
  }

  return (
    <>
      {currentStep}
      <LoadingOverlay
        id={'calendar-assistant-app-switching-overlay'}
        open={switchInProgress}
        label={t('*:str_MessagePleaseWait')}
        delay={0}
        key={'calendar-assistant-app-switching-overlay'}
      />
    </>
  );
};
